import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const STORAGE_KEY = "xpeditionz_cart_v1";
const MAX_QTY = 10;

// A line's identity is its product path + exact selected options combination
// — the same product with a different option choice (e.g. a different
// Vertical Drop) is a distinct cart line, not a quantity bump on the same one.
const makeLineId = (productPath, selectedOptions) => {
  const optKey = selectedOptions ? JSON.stringify(Object.entries(selectedOptions).sort()) : "";
  return `${productPath}::${optKey}`;
};

const readStoredCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readStoredCart);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item) => {
    const id = makeLineId(item.productPath, item.selectedOptions);
    setItems((prev) => {
      const existing = prev.find((i) => i.id === id);
      if (existing) {
        return prev.map((i) => (i.id === id ? { ...i, quantity: Math.min(i.quantity + (item.quantity || 1), MAX_QTY) } : i));
      }
      return [...prev, { ...item, id, quantity: item.quantity || 1 }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, Math.min(MAX_QTY, quantity)) } : i)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const subtotalCents = useMemo(() => items.reduce((sum, i) => sum + Math.round(i.unitPrice * 100) * i.quantity, 0), [items]);

  const value = { items, addItem, removeItem, updateQuantity, clearCart, totalCount, subtotalCents, isOpen, setIsOpen };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};
